import itemsData from '../data/items.json';

/**
 * Determines what items drop from enemies and chests.
 * Updated M13: armor and accessory drops added.
 */
export class LootTable {
  /**
   * Roll a drop for a regular or boss enemy.
   * @param {object} rng - An object with a next() method returning 0-1 and nextInt(min, max)
   * @param {string} enemyType - 'regular' or 'boss'
   * @returns {Array<{itemData: object, itemId: string}>} Array of items to drop (may be empty)
   */
  static rollEnemyDrop(rng, enemyType) {
    const drops = [];

    if (enemyType === 'boss') {
      // Boss always drops a random weapon + gold_pile_large
      const weaponKeys = Object.keys(itemsData.weapons);
      const weaponId = weaponKeys[rng.nextInt(0, weaponKeys.length - 1)];
      drops.push({
        itemData: { ...itemsData.weapons[weaponId] },
        itemId: weaponId,
      });

      drops.push({
        itemData: { ...itemsData.consumables.gold_pile_large },
        itemId: 'gold_pile_large',
      });

      // 50% chance to also drop armor (M13)
      if (rng.next() < 0.5) {
        const armorKeys = Object.keys(itemsData.armor);
        const armorId = armorKeys[rng.nextInt(0, armorKeys.length - 1)];
        drops.push({
          itemData: { ...itemsData.armor[armorId] },
          itemId: armorId,
        });
      }

      return drops;
    }

    // Regular enemy: 25% health potion, 45% gold_pile_small, 5% accessory, 25% nothing (M13)
    const roll = rng.next();
    if (roll < 0.25) {
      drops.push({
        itemData: { ...itemsData.consumables.health_potion },
        itemId: 'health_potion',
      });
    } else if (roll < 0.70) {
      drops.push({
        itemData: { ...itemsData.consumables.gold_pile_small },
        itemId: 'gold_pile_small',
      });
    } else if (roll < 0.75) {
      // 5% accessory drop (M13)
      const accKeys = Object.keys(itemsData.accessories);
      const accId = accKeys[rng.nextInt(0, accKeys.length - 1)];
      drops.push({
        itemData: { ...itemsData.accessories[accId] },
        itemId: accId,
      });
    }
    // else: 25% nothing

    return drops;
  }

  /**
   * Roll treasure for a chest or special room.
   * Updated M13: 60% weapon, 30% armor, 10% accessory distribution.
   * @param {object} rng - RNG instance
   * @param {number} floor - Current floor number
   * @returns {Array<{itemData: object, itemId: string}>} Array of items
   */
  static rollTreasure(rng, floor) {
    const drops = [];

    // Always some gold
    drops.push({
      itemData: { ...itemsData.consumables.gold_pile_large },
      itemId: 'gold_pile_large',
    });

    // Equipment drop: 60% weapon, 30% armor, 10% accessory (M13)
    if (floor >= 2 || rng.next() < 0.6) {
      const equipRoll = rng.next();
      if (equipRoll < 0.6) {
        // Weapon
        const weaponKeys = Object.keys(itemsData.weapons);
        const weaponId = weaponKeys[rng.nextInt(0, weaponKeys.length - 1)];
        drops.push({
          itemData: { ...itemsData.weapons[weaponId] },
          itemId: weaponId,
        });
      } else if (equipRoll < 0.9) {
        // Armor
        const armorKeys = Object.keys(itemsData.armor);
        const armorId = armorKeys[rng.nextInt(0, armorKeys.length - 1)];
        drops.push({
          itemData: { ...itemsData.armor[armorId] },
          itemId: armorId,
        });
      } else {
        // Accessory
        const accKeys = Object.keys(itemsData.accessories);
        const accId = accKeys[rng.nextInt(0, accKeys.length - 1)];
        drops.push({
          itemData: { ...itemsData.accessories[accId] },
          itemId: accId,
        });
      }
    }

    // 40% chance for a health potion
    if (rng.next() < 0.4) {
      drops.push({
        itemData: { ...itemsData.consumables.health_potion },
        itemId: 'health_potion',
      });
    }

    return drops;
  }
}
